import { useState, useEffect } from 'react';
import { useInputHelper, useFormValidation, unFlattenObject } from 'framework/form';
import { normalizeStoragePath } from '../../../documentStorage';
import useArtifactService from '../../artifactService';
import { getHumanReadableDataSize } from 'framework/utils';
import { getMbFromBytes } from '../../utils';

const useArtifactInputHelper = (props: any) => {
  const [artifactTypesObject, setArtifactTypesObject] = useState<Nullable<Dictionary<any>>>(null);
  const inputHelper = useInputHelper(props);
  const { getMaxElements: getMaxElementsFromValidation } = useFormValidation(props.source);
  const { getArtifactTypeObject } = useArtifactService();

  useEffect(() => {
    (async () => {
      const res = await getArtifactTypeObject(props.artifactTypeId);
      setArtifactTypesObject(res);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const collectionOnChangeCallback = inputHelper.getCollectionOnChangeCallback();

  const getArtifactType = () => props.artifactTypeId;

  const addArtifactToContext = (id: any) => {
    collectionOnChangeCallback((prev: any) => {
      return unFlattenObject({ [inputHelper.getSource()]: [...prev, id] });
    });
  };

  const removeArtifactFromContext = (id: any) => {
    const source = inputHelper.getSource();

    collectionOnChangeCallback((prev: any[]) => {
      const newValue = prev.filter(artifactId => {
        return normalizeStoragePath(artifactId) !== normalizeStoragePath(id);
      });

      return unFlattenObject({ [source]: newValue });
    });
  };

  const getAcceptExtensions = () => {
    return props.fileExtensions || (artifactTypesObject && artifactTypesObject.fileExtensions);
  };

  const getMaxMb = () => {
    const maxMb = getMbFromBytes(props.maximumSize || (artifactTypesObject && artifactTypesObject.maximumSize));

    return maxMb;
  };

  const getTooltip = () => inputHelper.getTooltip();

  const getMaxElements = () => {
    const maxElements = getMaxElementsFromValidation() || props.maxElements;
    return maxElements ?? 15;
  };

  const getHelpText = () => {
    const extensions = getAcceptExtensions();
    const maxElements = getMaxElements();

    const elementsText = `${maxElements ? `Up to ${maxElements} file${+maxElements === 1 ? '' : 's'}` : ''}`;
    const extensionsText = `${extensions ? `${elementsText ? ' ' : ''}(${extensions})` : ''}`;
    const fileSizeText = `${elementsText || extensionsText ? ', m' : 'M'}ax ${getHumanReadableDataSize(
      props.maximumSize || (artifactTypesObject && artifactTypesObject.maximumSize),
    )} ${+maxElements === 1 ? '' : 'each'}`;

    return elementsText + extensionsText + fileSizeText;
  };

  return {
    ...inputHelper,
    getAcceptExtensions,
    getArtifactType,
    addArtifactToContext: props.addArtifactToContext || addArtifactToContext,
    removeArtifactFromContext: props.removeArtifactFromContext || removeArtifactFromContext,
    getTooltip,
    getHelpText,
    getMaxMb,
    getMaxElements,
    getArtifactTypeObject,
  };
};

export default useArtifactInputHelper;
